/*
  Product grid carousel (snippets/product-grid-layout.liquid).

  A custom element, so the browser sets each carousel up when it is added to
  the page and tears it down when it is removed. That covers the theme
  editor's section reloads with no double binding, and the file can be
  loaded once per section without harm.

  The row itself is CSS scroll snap, so swiping needs no script. The script
  does two things:
  - Measures the page margin (the carousel's distance from each edge of the
    viewport, scrollbar excluded) into --pg-edge-start and --pg-edge-end, so
    the track runs full bleed and the snap positions sit on the margin.
  - Drives the arrows: each scrolls by one screen of cards and lands on a
    snap position the CSS defines (start, middle or end). They hide at
    either end and stay hidden when every card fits; an arrow that hides
    while focused passes focus to the other. Smooth scrolling is off under
    prefers-reduced-motion.
*/
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('product-grid-carousel')) {
    return;
  }

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Sub-pixel slack when comparing scroll positions.
  var SLACK = 2;

  class ProductGridCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pg-track]');
      this.prevButton = this.querySelector('[data-pg-prev]');
      this.nextButton = this.querySelector('[data-pg-next]');
      if (!this.track || !this.prevButton || !this.nextButton) {
        return;
      }

      this.frame = null;
      this.onChange = () => this.update();
      this.onResize = () => this.scheduleMeasure();
      this.onPrev = () => this.go(-1);
      this.onNext = () => this.go(1);

      this.track.addEventListener('scroll', this.onChange, { passive: true });
      this.prevButton.addEventListener('click', this.onPrev);
      this.nextButton.addEventListener('click', this.onNext);

      // The section spans the viewport, so its width follows the viewport's,
      // scrollbar included; this element's follows the container's.
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.closest('.product-grid') || this);
      this.resizeObserver.observe(this);

      this.measure();
    }

    disconnectedCallback() {
      if (!this.resizeObserver) {
        return;
      }
      this.track.removeEventListener('scroll', this.onChange);
      this.prevButton.removeEventListener('click', this.onPrev);
      this.nextButton.removeEventListener('click', this.onNext);
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
      if (this.frame) {
        window.cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }

    // Measure outside the ResizeObserver callback, so setting the edges
    // (which resizes the track) never loops back into it.
    scheduleMeasure() {
      if (this.frame) {
        return;
      }
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.measure();
      });
    }

    // The page margin on each side: from the viewport's edge (scrollbar
    // excluded) to the container's content box, which this element fills.
    measure() {
      var rect = this.getBoundingClientRect();
      var viewport = document.documentElement.clientWidth;
      this.style.setProperty('--pg-edge-start', Math.max(0, rect.left) + 'px');
      this.style.setProperty('--pg-edge-end', Math.max(0, viewport - rect.right) + 'px');
      this.update();
    }

    // The snap positions the CSS sets, as scroll offsets, in order.
    snapPositions() {
      var track = this.track;
      var max = track.scrollWidth - track.clientWidth;
      var origin = track.getBoundingClientRect().left + track.clientLeft - track.scrollLeft;
      var positions = [];

      Array.prototype.forEach.call(track.children, (card) => {
        var style = window.getComputedStyle(card);
        var align = style.scrollSnapAlign.split(' ').pop();
        if (align !== 'start' && align !== 'end') {
          return;
        }
        var box = card.getBoundingClientRect();
        var left = box.left - origin;
        var position = align === 'end'
          ? left + box.width + (parseFloat(style.scrollMarginRight) || 0) - track.clientWidth
          : left - (parseFloat(style.scrollMarginLeft) || 0);
        positions.push(Math.min(max, Math.max(0, position)));
      });

      return positions.sort((a, b) => a - b);
    }

    // One screen of cards: the column count, each card with its gap.
    screenWidth() {
      var card = this.track.firstElementChild;
      if (!card) {
        return this.clientWidth;
      }
      var gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
      var step = card.getBoundingClientRect().width + gap;
      var columns = parseInt(window.getComputedStyle(this).getPropertyValue('--pg-cols'), 10);
      return columns * step;
    }

    // Moves to the furthest snap position within one screen in that
    // direction, or to the nearest one if none is that close.
    go(direction) {
      var position = this.track.scrollLeft;
      var goal = position + direction * this.screenWidth();
      var ahead = this.snapPositions().filter((point) => {
        return direction > 0 ? point > position + SLACK : point < position - SLACK;
      });
      if (!ahead.length) {
        return;
      }
      // Nearest first.
      if (direction < 0) {
        ahead.reverse();
      }
      var within = ahead.filter((point) => {
        return direction > 0 ? point <= goal + SLACK : point >= goal - SLACK;
      });
      var target = within.length ? within[within.length - 1] : ahead[0];

      this.track.scrollTo({
        left: target,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    update() {
      var max = this.track.scrollWidth - this.track.clientWidth;
      var position = Math.abs(this.track.scrollLeft);
      var fits = max <= 1;

      var focused = document.activeElement;
      this.prevButton.hidden = fits || position <= 1;
      this.nextButton.hidden = fits || position >= max - 1;

      // A hidden arrow loses focus to the page. Hand it to the other arrow,
      // or to the first card when both are hidden.
      if (focused && focused.hidden && (focused === this.prevButton || focused === this.nextButton)) {
        var other = focused === this.prevButton ? this.nextButton : this.prevButton;
        var fallback = other.hidden ? this.track.querySelector('a[href]') : other;
        if (fallback) {
          fallback.focus({ preventScroll: true });
        }
      }
    }
  }

  window.customElements.define('product-grid-carousel', ProductGridCarousel);
})();
